// @ts-strict-ignore
import { Action, AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import {
  createObject,
  decomposeCellId,
  ErrorReportRawData,
  ReportRequestStatus,
  ReportUpdateHighlightMode,
} from 'algo-react-dataviz';
import axios, { AxiosResponse } from 'axios';
import { v4 } from 'uuid';
import {
  getSelectedPortfolios,
  hasSelectedPortfolios,
} from '../components/drawers/portfolioDrawerHelpers';
import { getTokenArray } from '../components/drawers/workspace-filter-drawer/workspaceFilterUtils';
import { isSerializedWorkspace } from '../components/report/helpers/importUtils';
import { baseUrl } from '../components/shared/environment';
import { getSandboxes } from '../hooks/useSandboxes';
import {
  ADHOC_REPORT_FOLDER,
  defaultReportDefinition,
  DESIGNER_SEQUENCE_ID,
  METADATA_SEQUENCE_ID,
  NotificationLevel,
} from '../shared/constants';
import {
  ImportToFolderData,
  ParentToChildRelationship,
  ReportDefinition,
  Sandbox,
  SelectedEntities,
  SelectedPortfolios,
  SerializedWorkspace,
  SerializedWorkspaceDefinition,
  WorkspaceData,
  WorkspaceFilter,
  WorkspacePayload,
  WorkspaceTab,
} from '../shared/dataTypes';
import {
  convertDefToDto,
  createReportGenerationTxId,
  extractWorkspacePayloadAttr,
  findTabId,
  getCommonRequestProperties,
  getReportWorkspacePayload,
  isReservedSequenceId,
  lookupWorkspaceReports,
  maxTabId,
  moveItem,
  nextRequestId,
  nextSequenceId,
} from '../shared/utils';
import {
  closeFolderDrawer,
  enqueueSnackbar,
  getErrorMessage,
  handleRequestTimerSetup,
  updateMetadataSandbox,
} from './ActionCreators';
import * as ActionTypes from './ActionTypes';
import { AppState, AppThunk, sendStompMessage } from './configureStore';
import { DesignerSource } from './designer/panel/designerSource';
import {
  addReportPendingRequest,
  getNextNodes,
  importReportDefinitions,
  regenerateReport,
  reportFailed,
  reportLoading,
  saveReport,
  sendReportUpdateMessage,
  setReportDefinition,
  setReportGenerationTxId,
  triggerReportRegen,
  updateReportData,
  updateReportPaths,
} from './ReportActionCreators';
import { openWorkspacePage } from './ui/actionCreators';

export const lookupTopLevelReports = (workspace: WorkspaceData): WorkspacePayload[] =>
  Object.values(workspace.tabs).flatMap(tab =>
    Object.values(tab.reports).filter(
      report =>
        (!report.parentSequenceId && report.parentSequenceId !== 0) ||
        report.parentSequenceId === DESIGNER_SEQUENCE_ID,
    ),
  );

const buildParentToChildRelationship = (workspace: WorkspaceData): ParentToChildRelationship => {
  const parentToChildRelationship: ParentToChildRelationship = {};

  Object.values(workspace.tabs).forEach(tab =>
    Object.values(tab.reports).forEach(report => {
      if (
        (report.parentSequenceId || report.parentSequenceId === 0) &&
        report.parentSequenceId !== DESIGNER_SEQUENCE_ID
      ) {
        const children: number[] = parentToChildRelationship[report.parentSequenceId] || [];
        children.push(report.sequenceId);
        parentToChildRelationship[report.parentSequenceId] = children;
      }
    }),
  );

  return parentToChildRelationship;
};

const reindexWorkspace = (
  workspaceData: WorkspaceData,
  nextTabId: number,
  seqIdMappings?: { [from: number]: number },
): { [tabId: number]: WorkspaceTab } => {
  const updatedTabs = {} as { [tabId: number]: WorkspaceTab };

  Object.values(workspaceData.tabs).forEach(tab => {
    const reindexedReports = {} as { [sequenceId: number]: WorkspacePayload };
    Object.values(tab.reports).forEach(report => {
      const newSequenceId = nextSequenceId();
      if (seqIdMappings) {
        seqIdMappings[report.sequenceId] = newSequenceId;
      }
      reindexedReports[newSequenceId] = { ...report, sequenceId: newSequenceId };
    });
    updatedTabs[nextTabId++] = { ...tab, reports: reindexedReports };
  });

  return updatedTabs;
};
const regenerateWorkspace = (
  isDrillThrough: boolean,
  workspaceData: WorkspaceData,
  parentSequenceId: number,
  selectedElements: string[],
): AppThunk => async (dispatch, getState) => {
  const topLevelReports = lookupTopLevelReports(workspaceData);
  if (isDrillThrough) {
    // this is a drill through to another workspace
    // we need to update sequence ids of the incoming workspace to aling with the existing one

    // at this point, we can set top level reports of the workspace being appended as being children of the parent report and drill throughs and live
    Object.values(workspaceData.tabs).forEach(tab => {
      Object.values(tab.reports).forEach(report => {
        if (
          (!report.parentSequenceId && report.parentSequenceId !== 0) ||
          report.parentSequenceId === DESIGNER_SEQUENCE_ID
        ) {
          report.parentSequenceId = parentSequenceId;
          report.drillThrough = true;
          report.live = true;
        }
      });
    });
  }

  const parentToChildRelationship = buildParentToChildRelationship(workspaceData);

  dispatch(updateWorkspace(workspaceData, isDrillThrough));
  // 1. identify a list of top level reports that need to be generated
  // 2. identify if any of the reports in the workspace are children to the above
  // 3. make sure that children generation is requested AFTER the parent is done generating
  // note, there is one exception to the "top level reports" definition above
  // specifically, if we drill through to another workspace (parentSequenceId and selectedElements are defined)
  // then top level reports are from the perspective of the new workspace despite the fact that
  // those top level reports are effectively drill through targets from wherever we drill through to the new workspace
  // this is also the reason why parentSelectedElements is set to selectedElements in the generate request below

  // dispatch parentToChildRelationship to the redux store so the info is available when results with top level report data come back from the server

  dispatch({
    type: ActionTypes.SET_PARENT_TO_CHILD_RELATIONSHIP,
    payload: { parentToChildRelationship, isAppendMode: isDrillThrough },
  });

  dispatch(setPersistWorkspacePortfolioSelection(hasSelectedPortfolios(workspaceData)));

  // send report generation request for top level reports
  topLevelReports.forEach(async r => {
    if (r.legacyReport && r.adhoc) {
      const reportRawData: ErrorReportRawData = {
        sequenceId: r.sequenceId,
        errMessage: 'Legacy adhoc reports are not supported.',
        date: new Date().getTime(),
      };
      dispatch(updateReportData(reportRawData));
      return;
    }
    const reportDefinition =
      getState().report.reportDefinition[r.sequenceId] ||
      (await axios
        .get<ReportDefinition>(`${baseUrl}api/reportDefinition`, {
          params: { path: r.reportPath, isLegacyReport: r.legacyReport },
        })
        .then(res => res.data)
        .catch(error => {
          dispatch(
            enqueueSnackbar(
              NotificationLevel.WARN,
              `Unable to open report: ${getErrorMessage(error)}`,
            ),
          );
          const reportRawData: ErrorReportRawData = {
            sequenceId: r.sequenceId,
            errMessage: getErrorMessage(error),
            date: new Date().getTime(),
          };
          dispatch(updateReportData(reportRawData));
        }));

    if (!reportDefinition) {
      return;
    }

    dispatch(setReportDefinition(r.sequenceId, reportDefinition));

    const requestId = `seq-${r.sequenceId}-req-${nextRequestId()}`;

    dispatch(
      addReportPendingRequest(
        r.sequenceId,
        requestId,
        ReportRequestStatus.PENDING,
        ReportUpdateHighlightMode.REPORT_CHANGE,
      ),
    );

    const whereClauseTokens = await getTokenArray(getState().workspace.data?.filter);

    dispatch(setReportGenerationTxId(r.sequenceId, createReportGenerationTxId(requestId)));
    sendStompMessage(
      {
        ...r,
        requestType: 'generate',
        parentSelectedElements: selectedElements,
        requestId,
        ...getCommonRequestProperties(getState()),
        reportDefinition,
        whereClauseTokens,
      },
      getState().user.tk || localStorage.getItem('id_token'),
      getState().report.reportData,
    )
      .then(() => {
        handleRequestTimerSetup(
          dispatch,
          requestId,
          r.sequenceId,
          getState().user.userInfo.serverConfigs.commTimeout,
        );
      })
      .catch((error: Error) => {
        dispatch(reportFailed(r.sequenceId, error.message));
      });
  });
};

export const setPersistWorkspacePortfolioSelection = (persistPortfolioSelection: boolean) =>
  ({
    type: ActionTypes.SET_PERSIST_WORKSPACE_PORTFOLIO_SELECTION,
    payload: persistPortfolioSelection,
  } as const);

export const appendSelectedWorkspace = (): AppThunk => (dispatch, getState) => {
  const { selectedFolderItem } = getState().drawers.folderDrawer || {};
  dispatch(fetchAndAppendWorkspaceTabs(selectedFolderItem?.props?.path));
};

export const fetchAndAppendWorkspaceTabs = (workspacePath: string): AppThunk => (
  dispatch,
  getState,
) => {
  axios
    .get<WorkspaceData>(`${baseUrl}api/workspaceDefinition`, {
      params: { path: workspacePath },
    })
    .then(response => {
      const fetchedWorkspaceData = response.data;
      const currentWorkspaceData = { ...getState().workspace.data };
      // add new tabs and put them in correct indexes
      let currentMaxTabId = maxTabId(getState().workspace.data.tabs);
      let newTabs = { ...getState().workspace.data.tabs };
      const insertTabsLength = Object.keys(fetchedWorkspaceData.tabs).length;

      Object.values(fetchedWorkspaceData.tabs).forEach(tab => {
        currentMaxTabId++;
        newTabs[currentMaxTabId] = tab;
      });
      currentWorkspaceData.tabs = newTabs;

      const seqIdMappings = {} as { [from: number]: number };
      currentWorkspaceData.tabs = reindexWorkspace(
        currentWorkspaceData,
        maxTabId(currentWorkspaceData.tabs) + 1,
        seqIdMappings,
      );

      // and then update parent ids to the newly assigned sequenceIds (if any)
      if (Object.keys(seqIdMappings).length) {
        Object.values(currentWorkspaceData.tabs).forEach(tab =>
          Object.values(tab.reports).forEach(report => {
            if (
              (report.parentSequenceId || report.parentSequenceId === 0) &&
              report.parentSequenceId !== DESIGNER_SEQUENCE_ID
            ) {
              report.parentSequenceId = seqIdMappings[report.parentSequenceId];
            }
          }),
        );
      }

      dispatch(regenerateWorkspace(false, currentWorkspaceData, undefined, undefined));

      // change tab to left most appended tab
      dispatch(
        changeSelectedTab(
          Number(
            Object.keys(currentWorkspaceData.tabs)[
              Object.keys(currentWorkspaceData.tabs).length - insertTabsLength
            ],
          ),
        ),
      );
    });
};

export const fetchSelectedWorkspace = (shouldOpenPage = true): AppThunk => (dispatch, getState) => {
  const { selectedFolderItem, sequenceId, selectedElements } =
    getState().drawers.folderDrawer || {};
  dispatch(fetchWorkspace(selectedFolderItem?.props?.path, sequenceId, selectedElements));

  if (shouldOpenPage) {
    dispatch(openWorkspacePage());
  }
};

export const fetchWorkspace = (
  workspacePath: string,
  parentSequenceId: number,
  selectedElements: string[],
): AppThunk => (dispatch, getState) => {
  const isDrillThrough =
    (parentSequenceId || parentSequenceId === 0) &&
    parentSequenceId !== DESIGNER_SEQUENCE_ID &&
    !!selectedElements;
  if (!isDrillThrough) {
    dispatch(updateWorkspaceStatus(true, null));
  }
  axios
    .get(`${baseUrl}api/workspaceDefinition`, {
      params: { path: workspacePath },
    })
    .then(async response => {
      const workspaceData: WorkspaceData = response.data;
      // We need to walk the incoming workspace and assign sequenceIds to each
      // visualization in the workspace as well as generate new tab ids
      // We also need to keep track of the old sequenceIds and their replacemement in seqIdMappings
      // so we can replace parent to child relationships in a second pass

      const seqIdMappings = {} as { [from: number]: number };
      // compute next available tab id from the current workspace (max tab id from current workspace + 1 ) if drill through, or 1 if opening workspace
      let nextTabId = isDrillThrough ? maxTabId(getState().workspace.data.tabs) + 1 : 1;
      workspaceData.tabs = reindexWorkspace(workspaceData, nextTabId, seqIdMappings);

      const reportDefInfo: {
        path: string;
        isLegacyReport: boolean;
        seqId: number;
      }[] = [];
      const needsParentIdUpdate = !!Object.keys(seqIdMappings).length;
      // and then update parent ids to the newly assigned sequenceIds (if any)
      // also get data to fetch reportDefs
      Object.values(workspaceData.tabs).forEach(tab =>
        Object.values(tab.reports).forEach(report => {
          reportDefInfo.push({
            path: report.reportPath,
            isLegacyReport: report.legacyReport,
            seqId: report.sequenceId,
          });
          if (needsParentIdUpdate) {
            if (
              (report.parentSequenceId || report.parentSequenceId === 0) &&
              report.parentSequenceId !== DESIGNER_SEQUENCE_ID
            ) {
              report.parentSequenceId = seqIdMappings[report.parentSequenceId];
            }
          }
        }),
      );

      //await all reportDefinitions
      await Promise.all(
        reportDefInfo.map(path =>
          path.path
            ? axios
                .get<ReportDefinition>(`${baseUrl}api/reportDefinition`, {
                  params: { path: path.path, isLegacyReport: path.isLegacyReport },
                })
                .then(res => dispatch(setReportDefinition(path.seqId, res.data)))
                .catch(error =>
                  dispatch(
                    enqueueSnackbar(
                      NotificationLevel.WARN,
                      `Unable to open report: ${getErrorMessage(error)}`,
                    ),
                  ),
                )
            : Promise.resolve(),
        ),
      );

      dispatch(
        regenerateWorkspace(
          isDrillThrough,
          {
            ...workspaceData,
            ...getSelectedPortfolios(getState(), null, workspaceData),
          },
          parentSequenceId,
          selectedElements,
        ),
      );
    })
    .catch((error: Error) => {
      dispatch(
        updateWorkspaceStatus(
          false,
          `Workspace with path ${workspacePath} failed to load: ${error.message}`,
        ),
      );

      dispatch(
        enqueueSnackbar(
          NotificationLevel.ERROR,
          `An error occurred when loading workspace ${workspacePath}`,
        ),
      );
    });
};

export const fetchSelectedWorkspaceTemplate = (shouldOpenPage = true): AppThunk => (
  dispatch,
  getState,
) => {
  const path = (getState().drawers.folderDrawer ?? {}).selectedFolderItem?.props?.path;

  axios
    .get<SerializedWorkspace>(`${baseUrl}api/templateWorkspace`, {
      params: { path },
    })
    .then(({ data }) => {
      dispatch(clearWorkspace());
      dispatch(importWorkspaceFile(data));
    })
    .catch((error: Error) => {
      dispatch(
        updateWorkspaceStatus(
          false,
          `Workspace template with path ${path} failed to load: ${error.message}`,
        ),
      );

      dispatch(
        enqueueSnackbar(
          NotificationLevel.ERROR,
          `An error occurred when loading workspace template ${path}`,
        ),
      );
    });

  if (shouldOpenPage) {
    dispatch(openWorkspacePage());
  }
};

export const importWorkspace = (serializedWorkspaceDefinition: SerializedWorkspaceDefinition) => ({
  type: ActionTypes.IMPORT_WORKSPACE,
  payload: serializedWorkspaceDefinition,
});

export const addSelectedReportToWorkspace = (): AppThunk => (dispatch, getState) => {
  const { selectedFolderItem, drillThrough, sequenceId, selectedElements, newTab } =
    getState().drawers.folderDrawer || {};

  dispatch(
    addReportToWorkspace({
      nodeId: selectedFolderItem?.props?.path,
      drillThrough,
      parentSequenceId: sequenceId,
      legacyReport: selectedFolderItem?.props?.isLegacy,
      adHoc: false,
      parentSelectedElements: selectedElements,
      toNewTab: newTab,
    }),
  );
};

export const addSelectedReportTemplateToWorkspace = (): AppThunk => (dispatch, getState) => {
  const { selectedFolderItem, drillThrough, sequenceId, selectedElements, newTab } =
    getState().drawers.folderDrawer || {};

  dispatch(
    addReportToWorkspace({
      nodeId: selectedFolderItem?.props?.path,
      drillThrough,
      parentSequenceId: sequenceId,
      isTemplate: true,
      legacyReport: selectedFolderItem?.props?.isLegacy,
      adHoc: false,
      parentSelectedElements: selectedElements,
      toNewTab: newTab,
    }),
  );
};

export const replaceReportInWorkspace = (): AppThunk => async (dispatch, getState) => {
  // Stash sequenceId and path in consts because the state goes away when we call closeFolderDrawer
  const {
    sequenceId,
    selectedFolderItem: {
      props: { path },
    },
  } = getState().drawers.folderDrawer;
  dispatch(closeFolderDrawer());

  try {
    const response = await axios.get<ReportDefinition>(`${baseUrl}api/reportDefinition`, {
      params: {
        path,
        isLegacyReport: !!getState().report.reportDefinition[sequenceId].legacyReport,
      },
    });

    dispatch(setReportDefinition(sequenceId, response.data));
    dispatch(regenerateReport(sequenceId));
    dispatch(reportLoading(sequenceId));
  } catch (error) {
    dispatch(
      enqueueSnackbar(NotificationLevel.WARN, `Unable to open report: ${getErrorMessage(error)}`),
    );
  }
};

export const importWorkspacesIntoSelectedFolder = (
  showImportModal: (data: any) => void,
  onTaskComplete: () => void,
): AppThunk => (dispatch, getState) =>
  axios
    .post<SerializedWorkspace | SerializedWorkspace[]>(
      `${baseUrl}api/convertWorkspaceJson`,
      JSON.parse(getState().workspace.selectedWorkspaceImportFile),
    )
    .then(response => {
      doImport(
        dispatch,
        getState,
        (isSerializedWorkspace(response.data) ? [response.data] : response.data).map(row => ({
          workspace: row[0],
          reports: row[1],
        })),
        'importWorkspaceConflicts',
        'importWorkspaces',
        showImportModal,
        onTaskComplete,
      );
    })
    .catch(error => {
      dispatch(
        enqueueSnackbar(NotificationLevel.ERROR, `File cannot be imported: ${error.message}`),
      );
      onTaskComplete();
    });

export const importReportsIntoSelectedFolder = (
  showImportModal: (reports: any) => void,
  onTaskComplete: () => void,
): AppThunk => (dispatch, getState) =>
  axios
    .post<ReportDefinition | ReportDefinition[]>(
      `${baseUrl}api/convertReportDefinitionJson`,
      JSON.parse(getState().workspace.selectedReportImportFile),
    )
    .then(response => {
      doImport(
        dispatch,
        getState,
        parseReports(response.data),
        'importReportConflicts',
        'importReports',
        showImportModal,
        onTaskComplete,
      );
    })
    .catch(error => {
      dispatch(
        enqueueSnackbar(NotificationLevel.ERROR, `File cannot be imported: ${error.message}`),
      );
      onTaskComplete();
    });

const doImport = async (
  dispatch: ThunkDispatch<AppState, unknown, Action>,
  getState: () => AppState,
  dataToBeImported: ImportToFolderData,
  conflictEndpoint: string,
  importEndpoint: string,
  showImportModal: (data: ImportToFolderData) => void,
  onTaskComplete: () => void,
) => {
  const { selectedFolderItem } = getState().drawers.folderDrawer || {};

  const response = await axios.post<boolean>(`${baseUrl}api/${conflictEndpoint}`, {
    target: selectedFolderItem.props.path,
    data: dataToBeImported,
  });

  if (response.data) {
    // There are conflicts. Need user input.
    showImportModal(dataToBeImported);
    return;
  }

  // No conflicts. Make call to allow the import to proceed.
  axios
    .post<boolean>(`${baseUrl}api/${importEndpoint}`, {
      target: selectedFolderItem.props.path,
      overrideExisting: true,
      data: dataToBeImported,
    })
    .then(() => {
      dispatch(enqueueSnackbar(NotificationLevel.SUCCESS, 'Import complete'));
    })
    .catch(error => {
      dispatch(enqueueSnackbar(NotificationLevel.ERROR, getErrorMessage(error)));
    });
  onTaskComplete();
};

const parseReports = (reportsObj: ReportDefinition | ReportDefinition[]): ReportDefinition[] => {
  if (Array.isArray(reportsObj)) {
    // Checking for dateContext in an effort to assure that the parsed structure is a report definition.
    const reports = reportsObj.filter(report => report.dateContext);
    if (reports.length > 0) {
      return reports;
    } else {
      throw new Error('File contains no reports.');
    }
  } else {
    // Check if the import file was generated usuing the 'Export to File' option.
    if (reportsObj.reportableType) {
      return [reportsObj];
    }
    throw new Error('Bad file format.');
  }
};

export interface AddReportToWorkspaceParams {
  nodeId?: string;
  parentSequenceId?: number;
  isTemplate?: boolean;
  drillThrough?: boolean;
  detailList?: boolean;
  legacyReport?: boolean;
  adHoc: boolean;
  parentSelectedElements?: string[];
  toNewTab?: boolean;
  reportDefinition?: ReportDefinition;
  adHocSequenceId?: number;
  hasChanges?: boolean;
}

export const addReportToWorkspace = ({
  nodeId = null,
  parentSequenceId = DESIGNER_SEQUENCE_ID,
  isTemplate = false,
  drillThrough = false,
  detailList = false,
  legacyReport = false,
  adHoc,
  parentSelectedElements,
  toNewTab = false,
  reportDefinition,
  adHocSequenceId = null,
  hasChanges,
}: AddReportToWorkspaceParams): AppThunk => async (dispatch, getState) => {
  const sequenceId = adHocSequenceId || nextSequenceId();
  const { source, sourceSequenceId } = getState().reportDesigner?.panelControl || {};
  const sandbox = getReportWorkspacePayload(
    source === DesignerSource.CLONE_FROM_WORKSPACE ? sourceSequenceId : parentSequenceId,
    getState(),
  )?.sandbox;

  const workspacePayload: WorkspacePayload = {
    layerId: v4(),
    sequenceId,
    reportPath: nodeId,
    legacyReport,
    drillThrough,
    detailList,
    adhoc: adHoc,
    live: drillThrough || detailList,
    parentSequenceId,
    parentSelectedElements,
    sandbox,
    hasChanges,
  };

  if (reportDefinition) {
    dispatch(setReportDefinition(sequenceId, reportDefinition));
  } else if (!detailList) {
    let response: AxiosResponse<ReportDefinition | string>;

    try {
      const apiUrl = isTemplate ? `${baseUrl}api/templateReport` : `${baseUrl}api/reportDefinition`;
      response = await axios.get<ReportDefinition | string>(apiUrl, {
        params: { path: nodeId, isLegacyReport: legacyReport },
      });

      reportDefinition = response.data as ReportDefinition;
    } catch (error) {
      dispatch(
        enqueueSnackbar(NotificationLevel.WARN, `Unable to open report: ${getErrorMessage(error)}`),
      );
      const reportRawData: ErrorReportRawData = {
        sequenceId: workspacePayload.sequenceId,
        errMessage: getErrorMessage(error),
        date: new Date().getTime(),
      };
      dispatch(updateReportData(reportRawData));
      return;
    }

    dispatch(setReportDefinition(sequenceId, reportDefinition));
  } else {
    // Adding a detail list. Dispatch a default report definition to allow
    // for operations like changing column widths which require a definition.
    dispatch(setReportDefinition(sequenceId, defaultReportDefinition));
  }
  dispatch({
    type: ActionTypes.ADD_REPORT_TO_WORKSPACE,
    payload: {
      workspacePayload,
      parentSequenceId,
      toNewTab,
    },
  });

  dispatch(setScrollPending(sequenceId));

  if ((parentSequenceId && parentSequenceId !== DESIGNER_SEQUENCE_ID) || parentSequenceId === 0) {
    // we are adding a child report
    // we need to update parent to child map
    const parentToChildRelationship: ParentToChildRelationship = {};

    parentToChildRelationship[parentSequenceId] = [workspacePayload.sequenceId];
    // dispatch parentToChildRelationship to the redux store so the info is available when results with top level report data come back from the server
    dispatch({
      type: ActionTypes.SET_PARENT_TO_CHILD_RELATIONSHIP,
      payload: { parentToChildRelationship, isAppendMode: true },
    });
  }

  // Don't generate the report if there is an adHocSequenceId. This means we already have the report
  // generated and only need to add the report to the main window.
  if (!adHocSequenceId) {
    const requestId = `seq-${workspacePayload.sequenceId}-req-${nextRequestId()}`;

    dispatch(
      addReportPendingRequest(
        workspacePayload.sequenceId,
        requestId,
        ReportRequestStatus.PENDING,
        ReportUpdateHighlightMode.REPORT_CHANGE,
      ),
    );

    const whereClauseTokens = await getTokenArray(getState().workspace.data?.filter);

    dispatch(
      setReportGenerationTxId(workspacePayload.sequenceId, createReportGenerationTxId(requestId)),
    );
    sendStompMessage(
      {
        ...workspacePayload,
        requestType: 'generate',
        requestId,
        ...getCommonRequestProperties(getState()),
        reportDefinition,
        whereClauseTokens,
      },
      getState().user.tk || localStorage.getItem('id_token'),
      getState().report.reportData,
    )
      .then(() => {
        handleRequestTimerSetup(
          dispatch,
          requestId,
          workspacePayload.sequenceId,
          getState().user.userInfo.serverConfigs.commTimeout,
        );
      })
      .catch(error => {
        dispatch(reportFailed(sequenceId, error.message));
      });
  }
};

export const setScrollPending = (sequenceId?: number, rowId?: string) => ({
  type: ActionTypes.SET_SCROLL_PENDING,
  payload: sequenceId,
});

export const goToParent = (sequenceId: number): AppThunk => (dispatch, getState) => {
  const parentSequenceId = extractWorkspacePayloadAttr('parentSequenceId', sequenceId, getState());
  const parentSelectedElement = extractWorkspacePayloadAttr(
    'selectedElements',
    parentSequenceId,
    getState(),
  )?.[0];
  const rowId = decomposeCellId(parentSelectedElement).rowId;
  dispatch({
    type: ActionTypes.REMOVE_DATA_AFTER_ROW,
    payload: { sequenceId: parentSequenceId, rowId: 0 },
  });
  dispatch(getNextNodes(false, parentSequenceId, rowId, undefined, undefined, true));
  dispatch(changeSelectedTab(findTabId(parentSequenceId, getState().workspace.data)));
  dispatch(setScrollPending(parentSequenceId, rowId));
};

export const removeReportFromWorkspace = (sequenceId: number): AppThunk => (dispatch, getState) => {
  // if sequenceId is DESIGNER_SEQUENCE_ID that means we are disposing the report preview which is not in the workspace
  // so no need to look at the workspace and children in this case
  if (!isReservedSequenceId(sequenceId)) {
    // here we need to recursively delete children of the report being closed (if any)
    // 1. get a list of all children from parentToChildRelationshipz
    getState()
      .workspace.parentToChildRelationship[sequenceId] // 2. if children found, iterate over the list and for each recursively dispatch removeReportFromWorkspace
      ?.forEach(childSequenceId => dispatch(removeReportFromWorkspace(childSequenceId)));
    // 3. then proceed to the deletion of the report associated with sequenceId as follow:

    // remove report from workspace
    dispatch({
      type: ActionTypes.REMOVE_REPORT_FROM_WORKSPACE,
      payload: { sequenceId },
    });
  }

  // delete report related data
  dispatch(deleteReportData(sequenceId));

  // send message to server to clean-up report reference
  sendStompMessage(
    {
      requestType: 'release',
      sequenceId,
    },
    getState().user.tk || localStorage.getItem('id_token'),
    getState().report.reportData,
  ).catch((error: Error) => {
    console.error(
      'Failed to release report reference for report with sequence id ' +
        sequenceId +
        ': ' +
        error.message,
    );
  });
};

export const saveWorkspace = (path: string, method: 'post' | 'put'): AppThunk => (
  dispatch,
  getState,
) => {
  const reports = Object.values(getState().workspace.data.tabs).flatMap(tab =>
    Object.values(tab.reports).filter(report => !report.detailList),
  );

  // If a report in the workspace has been modified then save it to an internal location.
  // If it's already an adhoc report then it is saved with a new path (the server will
  // remove the old adhoc report). This is to avoid sharing an adhoc report between
  // workspaces. Note that legacy reports should never be turned into adhoc reports.
  reports.forEach(workspacePayload => {
    const def = getState().report.reportDefinition[workspacePayload.sequenceId];
    if (
      def &&
      !def.legacyReport &&
      (!def.path || workspacePayload.hasChanges || def.path?.startsWith(ADHOC_REPORT_FOLDER))
    ) {
      const newReportPath = ADHOC_REPORT_FOLDER + '/' + v4();

      // Update the path in the workspace structure.
      dispatch(
        setWorkspaceReport({
          ...workspacePayload,
          reportPath: newReportPath,
          hasChanges: false,
        }),
      );

      // Save the report into the database with the new path.
      dispatch(saveReport(newReportPath, workspacePayload.sequenceId, 'put', true));

      // Update the report definition with the new path.
      dispatch(setReportDefinition(workspacePayload.sequenceId, { ...def, path: newReportPath }));
    }
  });

  axios({
    method,
    url: `${baseUrl}api/workspaceDefinition`,
    data: { ...getState().workspace.data, path },
    params: {
      persistPortfolioSelection: !!getState().workspace.persistPortfolioSelection,
    },
  })
    .then(({ data }) => {
      dispatch(enqueueSnackbar(NotificationLevel.SUCCESS, `Workspace ${path} saved`));
      dispatch(setWorkspacePathAndId(data.path, data.id));
    })
    .catch(error => {
      dispatch(
        enqueueSnackbar(
          NotificationLevel.ERROR,
          error.response.status === 409
            ? `An item with that name already exists: ${path}`
            : `Unable to save workspace: ${getErrorMessage(error)}`,
        ),
      );
    });
};

export const setWorkspacePathAndId = (path: string, id: string) => ({
  type: ActionTypes.SET_WORKSPACE_PATH_AND_ID,
  payload: { path, id },
});

export const updateWorkspaceStatus = (isLoading?: boolean, errMessage?: string) => ({
  type: ActionTypes.UPDATE_WORKSPACE_STATUS,
  payload: {
    isLoading,
    errMessage,
  },
});

export const updateWorkspace = (workspace: WorkspaceData, isDrillThrough: boolean): AppThunk => (
  dispatch,
  getState,
) => {
  let preExistingWorkspaceReports: WorkspacePayload[] = [];

  if (!isDrillThrough) {
    // this is NOT a drill through to another workspace
    // we need to release all "pre-existing" reports
    // look up all "pre-existing" reports first
    preExistingWorkspaceReports = lookupWorkspaceReports(getState().workspace.data);
  }

  // dispatch the new workspace being loaded (which will remove the previous one)
  dispatch({
    type: ActionTypes.UPDATE_WORKSPACE,
    payload: {
      newWorkspace: workspace,
      isDrillThrough,
    },
  });

  // now we are ready to dispose the "pre-existing" reports since the old workspace is gone
  if (!isDrillThrough) {
    // here we need to check if there is already an workspace loaded
    // if it is already loaded, release visualizations associated with it
    preExistingWorkspaceReports.forEach(wp => {
      // delete report related data
      dispatch(deleteReportData(wp.sequenceId));
      // send message to server to clean-up report reference
      sendStompMessage(
        {
          requestType: 'release',
          sequenceId: wp.sequenceId,
        },
        getState().user.tk || localStorage.getItem('id_token'),
        getState().report.reportData,
      ).catch((error: Error) => {
        console.error(
          'Failed to release report reference for report with sequence id ' +
            wp.sequenceId +
            ': ' +
            error.message,
        );
      });
    });
  }
};

export const getWorkspacePayload = dispatch => (
  sequenceId: number,
  workspaceData: WorkspaceData,
): WorkspacePayload => {
  const tabId = findTabId(sequenceId, workspaceData);

  const ret: WorkspacePayload = workspaceData?.tabs[tabId]?.reports[sequenceId];

  if (!ret) {
    dispatch(
      enqueueSnackbar(
        NotificationLevel.WARN,
        `Workspace payload for sequenceId ${sequenceId} not found`,
      ),
    );
  }

  return ret;
};

export const changeSelectedTab = (tabIndex: number) => ({
  type: ActionTypes.CHANGE_SELECTED_TAB,
  payload: { tabIndex },
});

export const setSelectedReportImportFile = (reportImportFile: string | ArrayBuffer) => ({
  type: ActionTypes.SET_SELECTED_REPORT_IMPORT_FILE,
  payload: reportImportFile,
});

export const setSelectedWorkspaceImportFile = (workspaceImportFile: string | ArrayBuffer) => ({
  type: ActionTypes.SET_SELECTED_WORKSPACE_IMPORT_FILE,
  payload: workspaceImportFile,
});

export const setMaximizedLayerId = (layerId: string | null) => ({
  type: ActionTypes.SET_MAXIMIZED_LAYER_ID,
  payload: layerId,
});

export const addNewTab = (): Action => ({
  type: ActionTypes.ADD_NEW_TAB,
});

export const renameTab = (tabId: number, name: string) => ({
  type: ActionTypes.RENAME_TAB,
  payload: { tabId, name },
});

export const removeTab = (tabId: number): AppThunk => (dispatch, getState) => {
  const tabToBeRemoved = getState().workspace.data.tabs[tabId];
  if (tabToBeRemoved) {
    Object.values(tabToBeRemoved.reports).forEach(report =>
      dispatch(removeReportFromWorkspace(report.sequenceId)),
    );
    dispatch({
      type: ActionTypes.REMOVE_TAB,
      payload: { tabId },
    });
  }
};

export const regenerateTopLevelReports = (): AppThunk => (dispatch, getState) => {
  const topLevelReports = lookupTopLevelReports(getState().workspace.data);

  topLevelReports.forEach(report => {
    dispatch(
      sendReportUpdateMessage(
        {
          sequenceId: report.sequenceId,
          requestId: `seq-${report.sequenceId}-req-${nextRequestId()}`,
          parentSelectedElements: report.parentSelectedElements,
          adhoc: true,
        },
        ReportUpdateHighlightMode.CELL_CHANGE,
        ReportRequestStatus.RECONNECTING,
      ),
    );
  });
};

export const clearTab = (tabId: number): AppThunk => (dispatch, getState) => {
  const tabToBeCleared = getState().workspace.data.tabs[tabId];

  Object.values(tabToBeCleared.reports).forEach(report =>
    dispatch(removeReportFromWorkspace(report.sequenceId)),
  );

  if (tabToBeCleared) {
    dispatch({
      type: ActionTypes.CLEAR_TAB,
      payload: { tabId },
    });
  }
};

export const rearrangeTabs = (sourceIndex: number, targetIndex: number): AppThunk => (
  dispatch,
  getState,
) => {
  const tabs = moveItem(
    Object.values(getState().workspace.data.tabs),
    sourceIndex,
    targetIndex,
  ).reduce((accum, tab, index) => {
    // convert to an object of WorkspaceTab type
    accum[(index + 1).toString()] = tab;
    return accum;
  }, {});
  dispatch({
    type: ActionTypes.SET_TABS,
    payload: tabs,
  });
};

export const clearWorkspace = (): AppThunk => (dispatch, getState) => {
  const reports = lookupWorkspaceReports(getState().workspace.data).map(wp => wp.sequenceId);
  dispatch({
    type: ActionTypes.CLEAR_WORKSPACE,
  });

  reports.forEach(sequenceId => {
    // delete report related data
    dispatch(deleteReportData(sequenceId));

    // send message to server to clean-up report reference
    sendStompMessage(
      {
        requestType: 'release',
        sequenceId,
      },
      getState().user.tk || localStorage.getItem('id_token'),
      getState().report.reportData,
    ).catch((error: Error) => {
      console.error(
        'Failed to release report reference for report with sequence id ' +
          sequenceId +
          ': ' +
          error.message,
      );
    });
  });
};

export const setWorkspaceReportPaths = (oldPath: string, newPath: string): AppThunk => (
  dispatch,
  getState,
) => {
  const { tabs } = getState().workspace.data;
  Object.keys(tabs).forEach(tabId => {
    Object.keys(tabs[tabId].reports).forEach(sequenceId => {
      const wp: WorkspacePayload = tabs[tabId].reports[sequenceId];
      if (wp.reportPath.startsWith(oldPath))
        dispatch(
          setWorkspaceReportPath(Number(sequenceId), wp.reportPath.replace(oldPath, newPath)),
        );
    });
  });

  dispatch(updateReportPaths(oldPath, newPath));
};

export const setWorkspaceReportPath = (sequenceId: number, reportPath: string): AnyAction => ({
  type: ActionTypes.UPDATE_WORKSPACE_REPORT_PATH,
  payload: { sequenceId, reportPath },
});

export const setWorkspaceReport = (wp: WorkspacePayload) =>
  ({
    type: ActionTypes.SET_WORKSPACE_REPORT,
    payload: wp,
  } as const);

export const setWorkspaceReportHasChanges = (sequenceId: number, hasChanges: boolean) =>
  ({
    type: ActionTypes.SET_WORKSPACE_REPORT_HAS_CHANGES,
    payload: { sequenceId, hasChanges },
  } as const);

export const deleteReportData = (sequenceId: number) =>
  ({ type: ActionTypes.DELETE_REPORT_DATA, payload: sequenceId } as const);

export const getSelectedElements = dispatch => (
  sequenceId: number,
  workspaceData: WorkspaceData,
): string[] => {
  const workspacePayload = getWorkspacePayload(dispatch)(sequenceId, workspaceData);

  return workspacePayload ? workspacePayload.selectedElements : [];
};

export const updateWorkspaceLayout = (tabId: number, layouts: ReactGridLayout.Layouts) => ({
  type: ActionTypes.UPDATE_WORKSPACE_LAYOUT,
  payload: { tabId, layouts },
});

export const setWorkspacePortfolioSelection = (ids: SelectedPortfolios): AppThunk => (
  dispatch,
  getState,
) => {
  dispatch({ type: ActionTypes.SET_WORKSPACE_PORTFOLIO_SELECTION, payload: ids });

  // Walk reports in the tab with tabId and trigger a report regeneration for
  // top level reports only (children will update as a result of parent update)
  triggerReportRegen(
    getState().workspace.data,
    {},
    dispatch,
    sequenceId => {
      const reportType = getState().report.reportDefinition[sequenceId].reportableType;
      return getState().user.userInfo.serverConfigs?.hashBucketReportableTypes.includes(reportType);
    },
    getState().report.reportDefinition,
  );
};

export const setWorkspaceFilterAndRegenerate = (filter: WorkspaceFilter): AppThunk => (
  dispatch,
  getState,
) => {
  dispatch({ type: ActionTypes.SET_WORKSPACE_FILTER, payload: filter });
  triggerReportRegen(getState().workspace.data, {}, dispatch);
};

export const setSelectedWorkspaceEntities = (entities: SelectedEntities): AppThunk => (
  dispatch,
  getState,
) => {
  dispatch({ type: ActionTypes.SET_SELECTED_WORKSPACE_ENTITIES, payload: entities });
  triggerReportRegen(getState().workspace.data, {}, dispatch);
};

const handleSandboxChildrenUpdates = (
  sequenceId: number,
  sandbox: string,
  owner?: string,
): AppThunk => (dispatch, getState) => {
  const parent = getState().workspace.parentToChildRelationship[sequenceId];
  if (parent?.length) {
    parent.forEach((child: number) => {
      dispatch(setWorkspaceDefinitionSandbox(child, sandbox, owner));
    });
  }
};

export const setWorkspaceDefinitionSandbox = (
  sequenceId: number,
  sandboxPath: string,
  owner?: string,
): AppThunk => (dispatch, getState) => {
  dispatch({
    type: ActionTypes.SET_WORKSPACE_SANDBOX,
    payload: {
      tabId: findTabId(sequenceId, getState().workspace.data),
      sequenceId,
      sandbox: { path: sandboxPath, owner },
    },
  });
};

export const createNewSandbox = (sequenceId: number): AppThunk => async dispatch => {
  const sandboxes = await getSandboxes(dispatch);

  if (sandboxes) {
    let id = 1;

    sandboxes.private.forEach(() => {
      if (sandboxes.private.includes(`Sandbox ${id}`)) {
        id++;
      }
    });

    return axios
      .post(`${baseUrl}api/createPrivateSandbox`, null, {
        params: { id },
      })
      .then(r => {
        if (sequenceId === METADATA_SEQUENCE_ID) {
          dispatch(updateMetadataSandbox(r.data));
        } else {
          dispatch(setWorkspaceReportHasChanges(sequenceId, true));
          dispatch(setWorkspaceDefinitionSandbox(sequenceId, r.data));
          dispatch(regenerateSandbox(sequenceId, r.data));
        }
      })
      .catch(error => {
        dispatch(
          enqueueSnackbar(
            NotificationLevel.ERROR,
            `Unable to create sandbox: ${getErrorMessage(error)}`,
          ),
        );
      });
  }
};

export const deleteSandbox = (sequenceId: number, sandbox: Sandbox): AppThunk => (
  dispatch,
  getState,
) => {
  const sandboxEndpoint = `${baseUrl}api/deleteSandbox`;
  dispatch(handleSandboxChildrenUpdates(sequenceId, null));
  dispatch(setWorkspaceDefinitionSandbox(sequenceId, null));
  return axios
    .post(sandboxEndpoint, sandbox)
    .then(() => {
      dispatch(regenerateSandbox(sequenceId, null));
    })
    .catch(error => {
      dispatch(
        enqueueSnackbar(
          NotificationLevel.ERROR,
          `Unable to delete sandbox: ${getErrorMessage(error)}`,
        ),
      );
    });
};

export const renameSandbox = (
  sequenceId: number,
  sandbox: Sandbox,
  newSandboxName: string,
): AppThunk => (dispatch, getState) => {
  const sandboxEndpoint = `${baseUrl}api/renameSandbox`;
  return axios
    .post(sandboxEndpoint, null, {
      params: {
        sandboxPath: sandbox.path,
        newSandboxName,
        sandboxOwner: sandbox.owner,
      },
    })
    .then(r => {
      dispatch(handleSandboxChildrenUpdates(sequenceId, r.data, sandbox.owner));
      dispatch(setWorkspaceDefinitionSandbox(sequenceId, r.data, sandbox.owner));
      dispatch(regenerateSandbox(sequenceId, r.data, sandbox.owner));
    })
    .catch(error => {
      dispatch(
        enqueueSnackbar(
          NotificationLevel.ERROR,
          `Unable to rename sandbox: ${getErrorMessage(error)}`,
        ),
      );
    });
};

export const clearPrivateSandbox = (sandbox: Sandbox): AppThunk => (dispatch, getState) => {
  const sandboxEndpoint = `${baseUrl}api/clearSandboxEdits`;
  return axios
    .post(sandboxEndpoint, sandbox)
    .then(() =>
      dispatch(enqueueSnackbar(NotificationLevel.SUCCESS, `Cleared sandbox ${sandbox.path}`)),
    )
    .catch(error => {
      dispatch(
        enqueueSnackbar(
          NotificationLevel.ERROR,
          `Unable to clear sandbox: ${getErrorMessage(error)}`,
        ),
      );
    });
};

export const switchToSandbox = (
  sequenceId: number,
  sandboxPath: string | null,
  owner?: string,
): AppThunk => (dispatch, getState) => {
  if (sequenceId === METADATA_SEQUENCE_ID) {
    dispatch(updateMetadataSandbox(sandboxPath, owner));
  } else {
    setWorkspaceReportHasChanges(sequenceId, true);
    dispatch(handleSandboxChildrenUpdates(sequenceId, sandboxPath, owner));
    dispatch(setWorkspaceDefinitionSandbox(sequenceId, sandboxPath, owner));
    dispatch(regenerateSandbox(sequenceId, sandboxPath, owner));
  }
};

const regenerateSandbox = (sequenceId: number, sandbox: string, owner?: string): AppThunk => (
  dispatch,
  getState,
) => {
  const requestId = `seq-${sequenceId}-req-${nextRequestId()}`;
  const workspacePayload = getReportWorkspacePayload(sequenceId, getState());
  dispatch(
    addReportPendingRequest(
      sequenceId,
      requestId,
      ReportRequestStatus.REGENERATING,
      ReportUpdateHighlightMode.REPORT_CHANGE,
    ),
  );

  const reportDefinition = getState().report.reportDefinition[sequenceId];

  dispatch(setReportGenerationTxId(sequenceId, createReportGenerationTxId(requestId)));
  sendStompMessage(
    {
      requestType: 'generate',
      requestId,
      adhoc: workspacePayload.adhoc,
      sequenceId,
      legacyReport: workspacePayload.legacyReport,
      ...getCommonRequestProperties(getState()),
      reportDefinition: reportDefinition.legacyReport
        ? reportDefinition
        : convertDefToDto(reportDefinition),
      sandbox: { path: sandbox, owner },
    },
    getState().user.tk || localStorage.getItem('id_token'),
    getState().report.reportData,
  );
};

export const importWorkspaceFile = (workspaceFile: SerializedWorkspace): AppThunk => (
  dispatch,
  getState,
) => {
  const [importedWorkspaceData, workspaceReports] = workspaceFile;

  // Set the workspace path to null
  const workspaceData: SerializedWorkspaceDefinition = {
    ...importedWorkspaceData,
    data: { ...importedWorkspaceData.data, path: null },
  };

  // Set each reports path to be null.
  const reports = createObject<[string, ReportDefinition], string, ReportDefinition>({
    items: Object.entries(workspaceReports),
    createKey: item => item[0],
    createValue: item => ({ ...item[1], path: null }),
  });

  dispatch(importReportDefinitions(reports));
  dispatch(importWorkspace(workspaceData));

  const preExistingWorkspaceReports = lookupWorkspaceReports(getState().workspace.data);
  preExistingWorkspaceReports.forEach(wp => {
    sendStompMessage(
      {
        requestType: 'release',
        sequenceId: wp.sequenceId,
      },
      getState().user.tk || localStorage.getItem('id_token'),
      getState().report.reportData,
    ).catch((error: Error) => {
      console.error(
        'Failed to release report reference for report with sequence id ' +
          wp.sequenceId +
          ': ' +
          error.message,
      );
    });
  });

  const topLevelReports = lookupTopLevelReports(workspaceData.data);
  topLevelReports.forEach(async report => {
    const reportDefinition = reports[report.sequenceId];
    const requestId = `seq-${report.sequenceId}-req-${nextRequestId()}`;
    dispatch(
      addReportPendingRequest(
        report.sequenceId,
        requestId,
        ReportRequestStatus.PENDING,
        ReportUpdateHighlightMode.REPORT_CHANGE,
      ),
    );

    const whereClauseTokens = await getTokenArray(getState().workspace.data?.filter);

    dispatch(setReportGenerationTxId(report.sequenceId, createReportGenerationTxId(requestId)));
    sendStompMessage(
      {
        ...reportDefinition,
        sequenceId: report.sequenceId,
        adhoc: true,
        requestType: 'generate',
        legacyReport: reportDefinition.legacyReport,
        parentSelectedElements: null,
        requestId,
        ...getCommonRequestProperties(getState()),
        reportDefinition,
        whereClauseTokens,
      },
      getState().user.tk || localStorage.getItem('id_token'),
      getState().report.reportData,
    )
      .then(() => {
        handleRequestTimerSetup(
          dispatch,
          requestId,
          report.sequenceId,
          getState().user.userInfo.serverConfigs.commTimeout,
        );
      })
      .catch((error: Error) => {
        dispatch(reportFailed(report.sequenceId, error.message));
      });
  });
};
